import { useState, forwardRef } from 'react';
import './BlogCard.css';
import type { BlogFeedItem } from '../types';

interface BlogCardProps {
  item: BlogFeedItem;
  index: number;
}

function formatRelativeDate(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMins = Math.floor(diffMs / (1000 * 60));

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function readingTime(wordCount: number): string {
  const wpm = 200;
  const minutes = Math.max(1, Math.round(wordCount / wpm));
  return `${minutes} min read`;
}

function importanceLabel(score: number): { text: string; className: string } {
  if (score >= 10) return { text: 'Landmark', className: 'blog-card__importance-badge--high' };
  if (score >= 8) return { text: 'Essential', className: 'blog-card__importance-badge--high' };
  if (score >= 6) return { text: 'Important', className: 'blog-card__importance-badge--mid' };
  if (score >= 4) return { text: 'Notable', className: 'blog-card__importance-badge--low' };
  return { text: 'Niche', className: 'blog-card__importance-badge--low' };
}

export const BlogCard = forwardRef<HTMLElement, BlogCardProps>(function BlogCard({ item, index }, ref) {
  const [showReason, setShowReason] = useState(false);
  const importance = importanceLabel(item.importanceScore);

  return (
    <article
      className="blog-card"
      id={`blog-card-${index}`}
      ref={ref}
    >
      <div className="blog-card__inner">
        <div className="blog-card__top-row">
          <span className="blog-card__subcategory">
            {item.subCategoryDisplayName}
          </span>
          <span className={`blog-card__importance-badge ${importance.className}`}>
            {importance.text}
          </span>
        </div>

        <h2 className="blog-card__title">{item.articleTitle}</h2>

        {item.importanceReason && (
          <div className="blog-card__reason-toggle">
            <button
              className="blog-card__reason-btn"
              onClick={() => setShowReason(!showReason)}
              aria-expanded={showReason}
            >
              Why {importance.text.toLowerCase()}?
              <span className={`blog-card__reason-chevron ${showReason ? 'blog-card__reason-chevron--open' : ''}`} aria-hidden="true">›</span>
            </button>
            {showReason && (
              <p className="blog-card__reason-text">{item.importanceReason}</p>
            )}
          </div>
        )}

        <p className="blog-card__tldr">{item.tldr}</p>

        {item.takeaways.length > 0 && (
          <section aria-label="Key takeaways">
            <p className="blog-card__takeaways-heading">Key takeaways</p>
            <ul className="blog-card__takeaways">
              {item.takeaways.map((point, i) => (
                <li key={i} className="blog-card__takeaway">
                  <span className="blog-card__takeaway-text">{point}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <footer className="blog-card__meta">
          <span className="blog-card__source">
            {item.sourceLogoUrl && (
              <img
                src={item.sourceLogoUrl}
                alt=""
                className="blog-card__source-logo"
                aria-hidden="true"
              />
            )}
            {item.sourceName}
          </span>

          {item.articleAuthor && (
            <>
              <span className="blog-card__meta-sep" aria-hidden="true">·</span>
              <span className="blog-card__meta-item">{item.articleAuthor}</span>
            </>
          )}

          {item.publishedAt && (
            <>
              <span className="blog-card__meta-sep" aria-hidden="true">·</span>
              <time
                className="blog-card__meta-item"
                dateTime={item.publishedAt}
                title={new Date(item.publishedAt).toLocaleString('en-GB')}
              >
                {formatRelativeDate(item.publishedAt)}
              </time>
            </>
          )}

          {item.wordCount > 0 && (
            <>
              <span className="blog-card__meta-sep" aria-hidden="true">·</span>
              <span className="blog-card__meta-item">{readingTime(item.wordCount)}</span>
            </>
          )}
        </footer>

        <a
          href={item.articleUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="blog-card__read-link"
          aria-label={`Read full article: ${item.articleTitle} (opens in new tab)`}
        >
          Read full article
          <span className="blog-card__read-icon" aria-hidden="true">↗</span>
        </a>
      </div>
    </article>
  );
});
